import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { ReactElement, ReactNode } from 'react';
import { MenuItem, Popup } from 'semantic-ui-react';
import { useBoolean } from 'usehooks-ts';

import Link from '@components/link';

import { useSideNavPopup } from './hooks';
import { useSideNavContext } from './side-nav-context';
// eslint-disable-next-line postcss-modules/no-unused-class
import s from './side-nav-item.scss';

export const getSideNavItemTooltipId = (id: string) =>
  `side-nav-item-tooltip-${id}`;

export interface SideNavItemOverrides {
  /**
   * Disables all interactions with the item, including navigation.
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Excludes the item from being rendered.
   * @default false
   */
  isHidden?: boolean;
  /**
   * Suffix component placed after the label text.
   */
  suffix?: ReactNode;
}

export interface SideNavItemProps extends SideNavItemOverrides {
  /**
   * Unique identifier for the nav item
   */
  id: string;
  /**
   * Icon to display alongside the label
   */
  icon?: ReactElement;
  /**
   * Label text for the nav item
   */
  label: string;
  /**
   * Path to navigate to on click
   */
  path: string;
  /**
   * Navigation category grouping
   */
  category?: 'assets' | 'general';
}

/**
 * Renders a single menu item within the side navigation menu.
 *
 * @see SideNav
 */
export const SideNavItem = ({
  isDisabled = false,
  isHidden = false,
  id,
  icon,
  label,
  path,
  suffix,
}: SideNavItemProps) => {
  useStyles(s);

  const { activeItem, isExpanded } = useSideNavContext();

  const isActive = activeItem === id;

  const popupProps = useSideNavPopup(id, label, isActive);

  const {
    value: isPopupVisible,
    setTrue: setPopupVisible,
    setFalse: setPopupHidden,
  } = useBoolean();

  if (isHidden) return null;

  const popupId = getSideNavItemTooltipId(id);

  const isPopupDisabled =
    isDisabled || (isExpanded && isActive) || popupProps.disabled;

  // Create a menu item element to use as the popup trigger
  const menuItem = (
    <MenuItem
      role="link"
      aria-label={label}
      aria-describedby={isPopupVisible ? popupId : undefined}
      aria-current={isActive ? 'page' : undefined}
      aria-disabled={isDisabled || undefined}
      disabled={isDisabled}
      as={isDisabled ? 'a' : Link}
      to={isDisabled ? undefined : path}
      active={isActive}
      className={s.root}
      onMouseEnter={setPopupVisible}
      onMouseLeave={setPopupHidden}
    >
      <div className={s.wrapper}>
        <span className={s.inner}>
          {icon && (
            <span
              className={classNames(
                s.icon,
                isActive && 'active',
                isDisabled && s.disabled,
              )}
            >
              {icon}
            </span>
          )}
          <span>{label}</span>
        </span>

        {suffix && (
          <span
            className={classNames(
              s.suffix,
              !isExpanded && s.mini,
              !isExpanded && 'animate__animated animate__fadeIn',
            )}
          >
            {suffix}
          </span>
        )}
      </div>
    </MenuItem>
  );

  // Use the Popup with trigger prop for simplicity
  return (
    <Popup
      {...popupProps}
      id={popupId}
      role="tooltip"
      className="animate__animated animate__fadeIn"
      content={popupProps.content}
      // TODO: Consider still showing popup for disabled items when collapsed
      disabled={isPopupDisabled}
      position="right center"
      on={['hover', 'focus']}
      onOpen={setPopupVisible}
      onClose={setPopupHidden}
      trigger={menuItem}
      hoverable
      flowing
    />
  );
};

export default SideNavItem;
